import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { DEFAULT_OG_IMAGE, getRouteMeta, SITE_ORIGIN } from './routeMeta';

const ROUTE_HEAD_ATTR = 'data-route-head';

function upsertMeta(selector: string, attrs: Record<string, string>): void {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const el = existing ?? document.createElement('meta');
  if (!existing) {
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== 'content') el.setAttribute(k, v);
    });
    el.setAttribute(ROUTE_HEAD_ATTR, '');
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute('content', attrs.content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearRouteJsonLd(): void {
  document.head
    .querySelectorAll<HTMLScriptElement>(`script[type="application/ld+json"][${ROUTE_HEAD_ATTR}]`)
    .forEach((node) => node.remove());
}

/**
 * Updates document.title plus per-route meta, canonical, OG/Twitter tags,
 * robots directive, and JSON-LD blocks based on the current pathname.
 *
 * Mount once per router subtree (typically inside layouts).
 */
export function useRouteHead(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = getRouteMeta(pathname);
    const canonicalUrl = `${SITE_ORIGIN}${meta.canonical}`;
    const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;

    document.title = meta.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: meta.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: meta.robots ?? 'index, follow' });
    upsertCanonical(canonicalUrl);

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: meta.title });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: meta.description,
    });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });

    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.title });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: meta.description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    clearRouteJsonLd();
    if (meta.jsonLd?.length) {
      meta.jsonLd.forEach((block) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute(ROUTE_HEAD_ATTR, '');
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }

    return () => {
      clearRouteJsonLd();
    };
  }, [pathname]);
}
